import { assertNonNullish, NonNullish } from 'utils/nullish-check';

type Config = {
  /**
   * Whether the region will be set from the [Navigator langauge](https://developer.mozilla.org/en-US/docs/Web/API/Navigator/language).
   * If false find the region from the IP address.
   */
  readonly useRegionFromNavigator: boolean;
  /**
   * Whether the sites will be set from the `hreflang` elements in the head.
   * If false, you'll have to set the sites manually.
   */
  readonly setSitesFromHreflangs: boolean;
  /** Whether to redirect to the selected site if the user has already selected a site. Only works if the user has selected a site. */
  readonly redirectIfHasSelected: boolean;
  readonly sites?: Array<Site> | undefined;
};

export type Site = {
  /** ISO 3166-1 alpha-2 */
  locale: string;
  /** ISO 3166-1 alpha-2 or "default" */
  region: string;
  url: string;
  isCurrent: boolean;
};

type RegionFilter = Partial<{
  region: string;
  locale: string;
}>;

const STORAGE_KEY = 'theme:selected-region';

export default class ShopRegions {
  private readonly config: Config;
  readonly sites: Array<Site> = [];

  constructor(config: Config) {
    this.config = config;

    if (this.config.sites) {
      this.sites.push(...this.config.sites);
    }

    if (config.setSitesFromHreflangs) {
      this.sites.push(...this.getSitesFromHreflangs());
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get('redirect')) {
      const site = this.getCurrentSite();

      this.saveSiteToStorage(site);
    }

    this.getRankedRegions = this.getRankedRegions.bind(this);
    this.getFilteredRegions = this.getFilteredRegions.bind(this);
    this.getIntendedSite = this.getIntendedSite.bind(this);
    this.getVisitorRegion = this.getVisitorRegion.bind(this);
  }

  private loadDependecies() {
    return new Promise<void>((resolve, reject) => {
      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        (error) => {
          if (error) {
            reject();
          } else {
            resolve();
          }
        },
      );
    });
  }

  private isSameOrigin(href: string) {
    try {
      return new URL(href, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private getSitesFromHreflangs() {
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]'),
    );
    const sites: Site[] = [];
    const seen = new Set<string>();

    for (const link of links) {
      const rawHreflang = link.getAttribute('hreflang')?.trim() ?? '';
      const hreflangLower = rawHreflang.toLowerCase();

      let locale: string;
      let region: string | undefined;

      if (hreflangLower === 'x-default') {
        locale = (
          theme.primaryLocale ??
          theme.currentLanguage ??
          Shopify.locale ??
          'en'
        ).toLowerCase();
        region = 'default';
      } else {
        [locale, region] = hreflangLower.split('-');

        assertNonNullish(locale);

        locale = locale.toLowerCase();

        if (!region || region.toLowerCase() === 'default') {
          region = 'default';
        } else {
          region = region.toUpperCase();
        }
      }

      const key = `${locale}-${region}`;

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);

      const isCurrent =
        this.isSameOrigin(link.href) &&
        locale === Shopify.locale?.toLowerCase() &&
        (region === 'default' || region === Shopify.country?.toUpperCase());

      sites.push({
        locale,
        region: region ?? 'default',
        url: link.href,
        isCurrent,
      });
    }

    return sites;
  }

  // IP / navigator country. Not stored preference. See: getRegion
  async getVisitorRegion() {
    await this.loadDependecies();

    if (this.config.useRegionFromNavigator) {
      return navigator.language.split('-').at(1)?.toUpperCase();
    }

    return Shopify.customerPrivacy?.getRegion()?.substring(0, 2).toUpperCase() || undefined;
  }

  // Prefer saved selection, else visitor geo. Used for site ranking.
  private async getRegion() {
    const stored = localStorage.getItem(STORAGE_KEY)?.split('-').at(1);

    return stored || (await this.getVisitorRegion());
  }

  private getLocale() {
    return (
      localStorage.getItem(STORAGE_KEY)?.split('-').at(0) ?? navigator.language.split('-').at(0)
    );
  }

  private saveSiteToStorage(site: Site) {
    localStorage.setItem(STORAGE_KEY, `${site.locale}-${site.region}`);
  }

  hasSelectedSite() {
    return localStorage.getItem(STORAGE_KEY) !== null;
  }

  getRankedRegions({ region, locale }: RegionFilter) {
    return this.sites
      .sort((a, b) => Number(b.region === 'default') - Number(a.region === 'default'))
      .sort((a, b) => {
        if (locale) {
          return Number(b.locale === locale) - Number(a.locale === locale);
        }

        return 0;
      })
      .sort((a, b) => {
        if (region) {
          return Number(b.region === region) - Number(a.region === region);
        }

        return 0;
      });
  }

  getFilteredRegions({ region, locale }: RegionFilter, shouldRank = false) {
    return (shouldRank ? this.getRankedRegions({ region, locale }) : this.sites).filter((site) => {
      if (region && site.region !== region) {
        return false;
      }

      if (locale && site.locale !== locale) {
        return false;
      }

      return true;
    });
  }

  getCurrentSite() {
    const currentSite = this.sites.find((site) => site.isCurrent);

    if (currentSite) {
      return currentSite;
    }

    return NonNullish(
      this.sites.find((site) => site.region.toLowerCase() === 'default'),
    );
  }

  /** Maps geo modal country codes to hreflang-derived sites. */
  getSiteForGeoCountry(countryCode: string) {
    const code = countryCode.toLowerCase();

    switch (code) {
      case 'dk':
        return this.sites.find((site) => site.locale === 'da');
      case 'de':
        return this.sites.find((site) => site.locale === 'de' && site.region === 'default');
      case 'no':
        return this.sites.find((site) => site.locale === 'no');
      case 'nl':
        return this.sites.find((site) => site.locale === 'nl');
      case 'gb':
        return this.sites.find((site) => site.locale === 'en' && site.region === 'GB');
      case 'en':
        return (
          this.sites.find((site) => site.locale === 'en' && site.region === 'default') ??
          this.sites.find((site) => site.region === 'default')
        );
      default:
        return undefined;
    }
  }

  getSiteNavigationUrl(site: Site) {
    const url = new URL(site.url);
    url.search = window.location.search;
    url.hash = window.location.hash;

    return url.toString();
  }

  selectSite(site: Site) {
    this.saveSiteToStorage(site);

    if (site !== this.getCurrentSite()) {
      const url = new URL(this.getSiteNavigationUrl(site));
      url.searchParams.set('redirect', '1');

      window.location.replace(url);
    }
  }

  async doAutomaticRedirect() {
    const site = await this.getIntendedSite();

    this.selectSite(site);
  }

  async shouldRedirect() {
    if (!this.config.redirectIfHasSelected || !this.hasSelectedSite()) {
      return false;
    }

    return this.getCurrentSite() !== (await this.getIntendedSite());
  }

  async shouldShowPrompt() {
    return !(await this.shouldRedirect());
  }

  async getIntendedSite() {
    const locale = this.getLocale();
    const region = await this.getRegion();

    return NonNullish(this.getRankedRegions({ region, locale }).at(0));
  }
}
